import React, { Fragment, FunctionComponent, useCallback, useMemo, useRef } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { ScrollBar } from '@yandex-360/orb-components/dist/components/ScrollBar';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import cn from 'classnames';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';

import { SvgImage } from '../../../../../../shared/components';
import { i18n, i18nMonthsNominative } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { FETCH_NEXT_CONFS_BATCH_THRESHOLD_PX } from '../../../../constants';
import { useIsMobileOneColLayout } from '../../../../hooks';
import {
    selectHistoryGroupedByDay,
    selectIsEmptyResult,
    selectIsFilterReset,
    selectIsFullFetchingFailed,
    selectIsFullLoading,
    selectIsNextBatchLoading,
} from '../../../../selectors';
import { FullLoadingSkeleton } from '../../../fetching/components/full-loading-skeleton';
import { NextBatchLoadingSkeleton } from '../../../fetching/components/next-batch-loading-skeleton';
import { resetAllFilters, setFilterArtifacts, setFilterDateRangeType, setFilterFromTo } from '../../../filter/slice';
import { forceHistoryFullReload, scrollThresholdReached } from '../../slice';
import { ConferenceForDay } from '../conferences-for-day';
import { Empty } from '../empty';
import styles from './styles.css';

export const ConferenceHistoryList: FunctionComponent = () => {
    const groupedConferencesHistory = useSelector(selectHistoryGroupedByDay);
    const isNextBatchLoading = useSelector(selectIsNextBatchLoading);
    const isFullLoading = useSelector(selectIsFullLoading);
    const isResultEmpty = useSelector(selectIsEmptyResult);
    const isFilterReset = useSelector(selectIsFilterReset);
    const isOneColLayout = useIsMobileOneColLayout();
    const actions = useActions({
        scrollThresholdReached,
        forceHistoryFullReload,
        setFilterDateRangeType,
        setFilterArtifacts,
        setFilterFromTo,
        resetAllFilters,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const onScroll = useCallback(() => {
        const container = containerRef.current as HTMLDivElement;
        if (
            container.scrollTop + container.offsetHeight >
            container.scrollHeight - FETCH_NEXT_CONFS_BATCH_THRESHOLD_PX
        ) {
            actions.scrollThresholdReached();
        }
    }, [actions]);
    const debouncedOnScroll = useMemo(() => debounce(onScroll, 100), [onScroll]);
    const onForceFullReloadClick = useCallback(() => {
        actions.forceHistoryFullReload();
    }, [actions]);
    const isFullFetchingFailed = useSelector(selectIsFullFetchingFailed);
    const onResetPress = useCallback(() => {
        countMetrika('meeting_history', 'reset_filters');
        actions.resetAllFilters();
    }, [actions]);
    if (isFullFetchingFailed) {
        return (
            <Empty
                content={
                    <SvgImage
                        imageName="conferencesHistoryStubFetchingError"
                        className={cn({ [styles.smallEmptyImage]: isOneColLayout })}
                    />
                }
                title={i18n('conferences-history', 'fetching-error')}
                button={<Button onClick={onForceFullReloadClick}>{i18n('conferences-history', 'refresh')}</Button>}
            />
        );
    }
    if (isFullLoading) {
        return <FullLoadingSkeleton />;
    }
    if (isResultEmpty) {
        return (
            <div className={cn(styles.conferenceHistoryList, { [styles.oneColLayout]: isOneColLayout })}>
                {isFilterReset && (
                    <Empty
                        content={
                            <SvgImage
                                imageName="conferencesHistoryStub"
                                className={cn({ [styles.smallEmptyImage]: isOneColLayout })}
                            />
                        }
                        title={i18n('conferences-history', 'empty-result-title')}
                    />
                )}
                {!isFilterReset && (
                    <Empty
                        content={
                            <SvgImage
                                imageName="conferencesHistoryStubActiveFilter"
                                className={cn({ [styles.smallEmptyImage]: isOneColLayout })}
                            />
                        }
                        title={i18n('conferences-history', 'empty-result-title-active-filter')}
                        description={i18n('conferences-history', 'change-filters-hint')}
                        button={<Button onPress={onResetPress}>{i18n('conferences-history', 'reset-filters')}</Button>}
                    />
                )}
            </div>
        );
    }
    return (
        <ScrollBar
            className={cn(styles.conferenceHistoryList, { [styles.oneColLayout]: isOneColLayout })}
            onScroll={debouncedOnScroll}
            ref={containerRef}
        >
            <table className={styles.table}>
                <tbody>
                    {groupedConferencesHistory.map((confsHistoryForMonth) => {
                        const monthIndex = new Date(confsHistoryForMonth[0][0].startedAt).getMonth();
                        return (
                            <Fragment key={monthIndex}>
                                <tr className={styles.dateRange}>
                                    <td colSpan={3}>
                                        <Text
                                            as="h2"
                                            typography={isOneColLayout ? 'heading-sm' : 'heading-xs'}
                                            className={styles.month}
                                        >
                                            {i18nMonthsNominative()[monthIndex]}
                                        </Text>
                                    </td>
                                </tr>
                                {confsHistoryForMonth.map((confsForDay) => (
                                    <ConferenceForDay key={confsForDay[0].shortUrlId} conferences={confsForDay} />
                                ))}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            {isNextBatchLoading && <NextBatchLoadingSkeleton />}
        </ScrollBar>
    );
};
